import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Plus, Save, X } from 'lucide-react';
import MainService from '../../services/MainService';

/**
 * Shared list + create modal for warehouse operational screens.
 * mode: receipt | stock | sort | pick | loading | dispatch | transfer | return | adjust | reports
 */
const WarehouseOpsPage = ({ title, subtitle, mode }) => {
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    WarehouseId: '',
    FromWarehouseId: '',
    ToWarehouseId: '',
    VoucherNo: '',
    PackageRef: '',
    Quantity: 1,
    Remarks: '',
    DriverId: '',
    VehicleId: '',
    Reason: '',
    QuantityBefore: 0,
    QuantityAfter: 0,
    AdjustmentQty: 0,
  });

  const canCreate = ['receipt', 'pick', 'dispatch', 'transfer', 'return', 'adjust'].includes(mode);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let resp;
      if (mode === 'receipt') resp = await MainService.WarehouseReceiptsList({ Skip: 0, Take: 100 });
      else if (mode === 'transfer') resp = await MainService.WarehouseTransfersList({ Skip: 0, Take: 100 });
      else if (mode === 'pick') resp = await MainService.WarehousePickListsList({ Skip: 0, Take: 100 });
      else if (mode === 'dispatch' || mode === 'loading') resp = await MainService.WarehouseDispatchesList({ Skip: 0, Take: 100 });
      else if (mode === 'return') resp = await MainService.WarehouseReturnReceiptsList({ Skip: 0, Take: 100 });
      else if (mode === 'adjust') resp = await MainService.WarehouseStockAdjustmentsList({ Skip: 0, Take: 100 });
      else resp = await MainService.WarehouseStockList({ Skip: 0, Take: 100 });
      setRows(resp?.Data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    load();
    (async () => {
      try {
        const wh = await MainService.WarehousesInDropDown();
        setWarehouses(Array.isArray(wh) ? wh : []);
        if (mode === 'dispatch') {
          try {
            const dr = await MainService.DriversInPickupAllocation();
            setDrivers(Array.isArray(dr) ? dr : dr?.Data || []);
          } catch {
            setDrivers([]);
          }
          const vh = await MainService.VehiclesInDropDown();
          setVehicles(Array.isArray(vh) ? vh : []);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [load, mode]);

  const handleSave = async (e) => {
    e.preventDefault();
    let payload;
    let resp;
    if (mode === 'receipt') {
      payload = {
        WarehouseId: Number(form.WarehouseId),
        Date: new Date().toISOString(),
        Remarks: form.Remarks,
        Details: [{ VoucherNo: form.VoucherNo, PackageRef: form.PackageRef, Quantity: Number(form.Quantity) || 1 }],
      };
      resp = await MainService.SaveWarehouseReceipt(payload);
    } else if (mode === 'transfer') {
      payload = {
        FromWarehouseId: Number(form.FromWarehouseId),
        ToWarehouseId: Number(form.ToWarehouseId),
        Date: new Date().toISOString(),
        Remarks: form.Remarks,
        Details: [{ VoucherNo: form.VoucherNo, PackageRef: form.PackageRef, Quantity: Number(form.Quantity) || 1 }],
      };
      resp = await MainService.SaveWarehouseTransfer(payload);
    } else if (mode === 'dispatch') {
      payload = {
        WarehouseId: Number(form.WarehouseId),
        DriverId: form.DriverId || null,
        VehicleId: form.VehicleId || null,
        Date: new Date().toISOString(),
        Remarks: form.Remarks,
        Details: [{ VoucherNo: form.VoucherNo, PackageRef: form.PackageRef }],
      };
      resp = await MainService.SaveWarehouseDispatch(payload);
    } else if (mode === 'pick') {
      payload = [{ WarehouseId: Number(form.WarehouseId), VoucherNo: form.VoucherNo, PackageRef: form.PackageRef }];
      resp = await MainService.SaveWarehousePickList(payload);
    } else if (mode === 'return') {
      payload = {
        WarehouseId: Number(form.WarehouseId),
        VoucherNo: form.VoucherNo,
        PackageRef: form.PackageRef,
        Reason: form.Reason,
        Remarks: form.Remarks,
        Date: new Date().toISOString(),
      };
      resp = await MainService.SaveWarehouseReturnReceipt(payload);
    } else if (mode === 'adjust') {
      payload = {
        WarehouseId: Number(form.WarehouseId),
        VoucherNo: form.VoucherNo,
        PackageRef: form.PackageRef,
        QuantityBefore: Number(form.QuantityBefore) || 0,
        QuantityAfter: Number(form.QuantityAfter) || 0,
        AdjustmentQty: Number(form.AdjustmentQty) || 0,
        Reason: form.Reason,
        Date: new Date().toISOString(),
      };
      resp = await MainService.SaveWarehouseStockAdjustment(payload);
    }
    if (resp === 'Success') {
      setOpen(false);
      load();
    } else {
      alert(resp || 'Failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between rounded-lg border bg-white p-6 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold">{title}</h1>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={load} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {canCreate && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white"
            >
              <Plus className="h-4 w-4" />
              New
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Ref / Voucher</th>
              <th className="px-3 py-2 text-left">Warehouse</th>
              <th className="px-3 py-2 text-left">Package</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                  No records
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.Id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">{r.RefNo || r.VoucherNo || r.Id}</td>
                  <td className="px-3 py-2">
                    {r.Warehouse?.Name || r.FromWarehouse?.Name || '—'}
                    {r.ToWarehouse?.Name ? ` → ${r.ToWarehouse.Name}` : ''}
                  </td>
                  <td className="px-3 py-2">{r.PackageRef || r.VoucherNo || '—'}</td>
                  <td className="px-3 py-2">{r.Status || '—'}</td>
                  <td className="px-3 py-2">
                    {r.Date
                      ? String(r.Date).slice(0, 10)
                      : r.LastMovedDate
                        ? String(r.LastMovedDate).slice(0, 10)
                        : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form onSubmit={handleSave} className="w-full max-w-md space-y-3 rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">New — {title}</h3>
              <button type="button" onClick={() => setOpen(false)}>
                <X />
              </button>
            </div>

            {mode === 'transfer' ? (
              <>
                <select
                  required
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={form.FromWarehouseId}
                  onChange={(e) => setForm((p) => ({ ...p, FromWarehouseId: e.target.value }))}
                >
                  <option value="">From warehouse</option>
                  {warehouses.map((w) => (
                    <option key={w.Id} value={w.Id}>
                      {w.Name}
                    </option>
                  ))}
                </select>
                <select
                  required
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={form.ToWarehouseId}
                  onChange={(e) => setForm((p) => ({ ...p, ToWarehouseId: e.target.value }))}
                >
                  <option value="">To warehouse</option>
                  {warehouses.map((w) => (
                    <option key={w.Id} value={w.Id}>
                      {w.Name}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <select
                required
                className="w-full rounded border px-3 py-2 text-sm"
                value={form.WarehouseId}
                onChange={(e) => setForm((p) => ({ ...p, WarehouseId: e.target.value }))}
              >
                <option value="">Warehouse</option>
                {warehouses.map((w) => (
                  <option key={w.Id} value={w.Id}>
                    {w.Name}
                  </option>
                ))}
              </select>
            )}

            <input
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Voucher No"
              value={form.VoucherNo}
              onChange={(e) => setForm((p) => ({ ...p, VoucherNo: e.target.value }))}
            />
            <input
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Package Ref"
              value={form.PackageRef}
              onChange={(e) => setForm((p) => ({ ...p, PackageRef: e.target.value }))}
            />

            {['receipt', 'transfer', 'pick'].includes(mode) && (
              <input
                type="number"
                min="1"
                className="w-full rounded border px-3 py-2 text-sm"
                placeholder="Quantity"
                value={form.Quantity}
                onChange={(e) => setForm((p) => ({ ...p, Quantity: e.target.value }))}
              />
            )}

            {mode === 'dispatch' && (
              <>
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={form.DriverId}
                  onChange={(e) => setForm((p) => ({ ...p, DriverId: e.target.value }))}
                >
                  <option value="">Driver</option>
                  {drivers.map((d) => (
                    <option key={d.Id} value={d.Id}>
                      {d.Name}
                    </option>
                  ))}
                </select>
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={form.VehicleId}
                  onChange={(e) => setForm((p) => ({ ...p, VehicleId: e.target.value }))}
                >
                  <option value="">Vehicle</option>
                  {vehicles.map((v) => (
                    <option key={v.Id} value={v.Id}>
                      {v.Number}
                    </option>
                  ))}
                </select>
              </>
            )}

            {['return', 'adjust'].includes(mode) && (
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                placeholder="Reason"
                value={form.Reason}
                onChange={(e) => setForm((p) => ({ ...p, Reason: e.target.value }))}
              />
            )}

            {mode === 'adjust' && (
              <>
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  placeholder="Qty Before"
                  value={form.QuantityBefore}
                  onChange={(e) => setForm((p) => ({ ...p, QuantityBefore: e.target.value }))}
                />
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  placeholder="Qty After"
                  value={form.QuantityAfter}
                  onChange={(e) => setForm((p) => ({ ...p, QuantityAfter: e.target.value }))}
                />
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  placeholder="Adjustment Qty"
                  value={form.AdjustmentQty}
                  onChange={(e) => setForm((p) => ({ ...p, AdjustmentQty: e.target.value }))}
                />
              </>
            )}

            <textarea
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Remarks"
              value={form.Remarks}
              onChange={(e) => setForm((p) => ({ ...p, Remarks: e.target.value }))}
            />

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded border px-3 py-2 text-sm">
                Cancel
              </button>
              <button type="submit" className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm text-white">
                <Save className="h-4 w-4" />
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default WarehouseOpsPage;
